import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import { ptoRequestsApi } from '../services/api';
import { PTORequestCreate, PTORequestType } from '../types';
import { useNavigate } from 'react-router-dom';

const PTORequest: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<PTORequestCreate>({
    pto_type: 'vacation' as PTORequestType,
    start_date: '',
    end_date: '',
    total_days: 0,
    is_half_day_start: false,
    is_half_day_end: false,
    reason: '',
    employee_name: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Create request mutation
  const createRequestMutation = useMutation(
    (data: PTORequestCreate) => ptoRequestsApi.createRequest(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('myBalances');
        queryClient.invalidateQueries('myPTOHistory');
        navigate('/pto/my-pto');
      },
    }
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Auto-calculate total days
    if (name === 'start_date' || name === 'end_date') {
      const startDate = name === 'start_date' ? value : formData.start_date;
      const endDate = name === 'end_date' ? value : formData.end_date;

      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

        let totalDays = diffDays;
        if (formData.is_half_day_start) totalDays -= 0.5;
        if (formData.is_half_day_end) totalDays -= 0.5;

        setFormData(prev => ({ ...prev, total_days: totalDays }));
      }
    }
  };

  const handleHalfDayChange = (field: 'is_half_day_start' | 'is_half_day_end') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setFormData(prev => {
      const newData = { ...prev, [field]: checked };

      // Recalculate total days
      if (newData.start_date && newData.end_date) {
        const start = new Date(newData.start_date);
        const end = new Date(newData.end_date);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        let totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

        if (newData.is_half_day_start) totalDays -= 0.5;
        if (newData.is_half_day_end) totalDays -= 0.5;

        newData.total_days = totalDays;
      }

      return newData;
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }

    if (formData.start_date && formData.end_date) {
      if (new Date(formData.start_date) > new Date(formData.end_date)) {
        newErrors.end_date = 'End date must be after start date';
      }
    }

    if (formData.total_days <= 0) {
      newErrors.total_days = 'Total days must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    createRequestMutation.mutate(formData);
  };

  const getPTOTypeLabel = (type: string): string => {
    return type.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Request Time Off
        </Typography>

        {createRequestMutation.isError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {(createRequestMutation.error as any)?.response?.data?.detail ||
              'Failed to submit PTO request. Please try again.'}
          </Alert>
        )}

        {createRequestMutation.isSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            PTO request submitted successfully!
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="employee_name"
                label="Employee Name"
                value={formData.employee_name}
                onChange={handleChange}
                placeholder="Enter employee name (for manager submissions)"
                helperText="Leave blank if requesting for yourself"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                name="start_date"
                label="Start Date"
                value={formData.start_date}
                onChange={handleChange}
                error={Boolean(errors.start_date)}
                helperText={errors.start_date}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: new Date().toISOString().split('T')[0]
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_half_day_start}
                    onChange={handleHalfDayChange('is_half_day_start')}
                    name="is_half_day_start"
                  />
                }
                label="Half day"
                sx={{ mt: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                name="end_date"
                label="End Date"
                value={formData.end_date}
                onChange={handleChange}
                error={Boolean(errors.end_date)}
                helperText={errors.end_date}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: formData.start_date || new Date().toISOString().split('T')[0]
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_half_day_end}
                    onChange={handleHalfDayChange('is_half_day_end')}
                    name="is_half_day_end"
                  />
                }
                label="Half day"
                sx={{ mt: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                disabled
                name="total_days"
                label="Total Days"
                value={formData.total_days.toFixed(1)}
                helperText="Automatically calculated based on dates"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                name="reason"
                label="Reason (Optional)"
                value={formData.reason}
                onChange={handleChange}
                placeholder="Provide a brief reason for your time off request"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/pto/my-pto')}
                  disabled={createRequestMutation.isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={createRequestMutation.isLoading ? <CircularProgress size={20} /> : <SendIcon />}
                  disabled={createRequestMutation.isLoading}
                >
                  {createRequestMutation.isLoading ? 'Submitting...' : 'Submit Request'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default PTORequest;
